// Modified by AI on 06/12/2026. Edit #1.
import {
    chain, debounce, get, isArray, isEmpty,
} from 'lodash';

import React, {
    useState, useEffect, useContext, useCallback, useRef, useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PositionHttpService from '../api/PositionHttpService';
import LoadingContext, { LOADING_AREAS } from './LoadingContext';
import NotificationContext from './NotificationContext';
import SettingsContext from './SettingsContext';
import { TRACKING_EVENTS } from '../constants/TrackingConstants';
import TrackingContext from './TrackingContext';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';
import ComplianceService from '../services/ComplianceService';
import Logger from '../services/logger';
import { manualQualifySteps } from '../constants/ManualQualifyConstants';
import {
    positionEvents,
    PositionStatuses,
    RecalibrationFlowTypes,
} from '../constants/Position';
import ATS_TYPE from '../constants/AtsType';
import EmployerContext from './EmployerContext';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';

export const POSITION_EVENTS = {
    POSITION_CRITERIA_CHANGED: 'POSITION_CRITERIA_CHANGED',
};

const PositionContext = React.createContext({
    position: {},
    setPosition: () => {},
    positionStatus: '',
    successAccountManagerEmail: '',
    getSuccessAccountManagerEmail: () => {},
});

export const REFRESH_PROGRESS_AI_SCORE_DEBOUNCE_TIME_MS = 1000;

const initialCheckedCandidatesState = Object.values(EmployerWorkflowStatus).reduce((state, current) => {
    // eslint-disable-next-line no-param-reassign
    state[current] = {};
    return state;
}, {});

export const PositionContextProvider = PositionContext.Provider;

// Modified by AI on 06/12/2026. Edit #2.
export const PositionApp = withRouter(({ children, location }) => {
    const positionId = useMemo(() => {
        const match = matchPath(location.pathname, {
            path: '*/position/:positionId([0-9a-f]{24})/*',
            exact: false,
            strict: false,
        });

        return get(match, 'params.positionId', null);
    }, [location.pathname]);

    const { showErrorNotification } = useContext(NotificationContext);
    const { startFetchingData, stopFetchingData } = useContext(LoadingContext);
    const { trackEvent } = useContext(TrackingContext);
    const { employer } = useContext(EmployerContext);
    const {
        systemConfiguration: {
            progressAI = { sufficientScoreInPercent: 60 },
        },
    } = useContext(SettingsContext);

    // Modified by AI on 06/12/2026. Edit #3.
    // Fetch position data via React Query
    const {
        data: positionData,
        error: positionError,
        isLoading: positionIsLoading,
        refetch: refetchPosition,
    } = useQuery({
        queryKey: QUERY_KEYS.position(positionId),
        queryFn: () => PositionHttpService.getPosition(positionId),
        enabled: !!positionId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    const position = positionData || {};

    // Local state (not from server)
    const [positionStatus, setPositionStatus] = useState('');
    const [isPositionCalibrated, setIsPositionCalibrated] = useState(false);
    const [isPositionInRecalibrationEditFlow, setIsPositionInRecalibrationEditFlow] = useState(false);
    const [isPositionInRecalibrationNewFlow, setIsPositionInRecalibrationNewFlow] = useState(false);
    const positionAIProgressScore = useRef(0);
    // Modified by AI on 06/30/2026. Edit #1.
    const [positionEventTrigger, setPositionEventTrigger] = useState(0);
    const [positionEventData, setPositionEventData] = useState(null);
    const [successAccountManagerEmail, setSuccessAccountManagerEmail] = useState('support@talenya.com');

    const [positionHasRemoteLocation, setPositionHasRemoteLocation] = useState();
    const [isPositionGdprCompliant, setIsPositionGdprCompliant] = useState();
    const positionGdprCheckRef = useRef({ locationsKey: null, result: null });
    const positionGdprLocationsRef = useRef({ locationsKey: null, result: null });

    const [checkedCandidates, setCheckedCandidates] = useState(initialCheckedCandidatesState);

    const isAtsPosition = !!position?.atsSettings?.atsJobId;
    const isPcPosition = position?.atsSettings?.atsType === ATS_TYPE.Paycor;
    const isPositionInManualSelectionStep = position?.manualQualifyData?.step === manualQualifySteps.ManualSelection;
    const isPositionInManualMessagingStep = position?.manualQualifyData?.step === manualQualifySteps.ManualMessaging;
    const positionRecalibrationStatus = position?.recalibrationStatus?.current;
    const positionIsNotInRecalibrationFlow = !positionRecalibrationStatus || positionRecalibrationStatus === RecalibrationFlowTypes.none;

    const uncheckAllCandidates = useCallback(() => {
        setCheckedCandidates(initialCheckedCandidatesState);
    }, []);

    const removeCheckedCandidate = useCallback((talentIds, workflowStep) => {
        const checkedCandidatesStep = checkedCandidates[workflowStep];
        if (!checkedCandidatesStep) {
            return;
        }
        const ids = isArray(talentIds) ? talentIds : [talentIds];
        const candidatesToRemove = {};
        ids.forEach((id) => {
            if (checkedCandidatesStep[id]) {
                candidatesToRemove[id] = false;
            }
        });

        setCheckedCandidates({
            ...checkedCandidates,
            [workflowStep]: {
                ...checkedCandidatesStep,
                ...candidatesToRemove,
            },
        });
    }, [setCheckedCandidates, checkedCandidates]);

    // Modified by AI on 06/12/2026. Edit #4.
    // Side effects when position data changes
    useEffect(() => {
        if (positionData) {
            const status = positionData?.status?.current || '';
            const lastPositionWizardEvent = chain(positionData?.events)
                .filter({ flowType: 'positionWizard' })
                .last()
                .get('eventType')
                .value();

            const isCompleteEvent = [positionEvents.wizardComplete, positionEvents.fastRating].includes(lastPositionWizardEvent);

            setIsPositionCalibrated(isCompleteEvent);
            setIsPositionInRecalibrationEditFlow(positionData?.recalibrationStatus?.current === RecalibrationFlowTypes.edit);
            setIsPositionInRecalibrationNewFlow(positionData?.recalibrationStatus?.current === RecalibrationFlowTypes.new);
            setPositionStatus(status);
            setIsPositionGdprCompliant(!!positionData?.isGdprCompliant);
            setPositionHasRemoteLocation(!!positionData?.hasRemoteLocation);

            if (!isCompleteEvent) {
                refreshProgressAIScore(positionData);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionData]);

    // Modified by AI on 06/12/2026. Edit #5.
    // LoadingContext integration
    useEffect(() => {
        if (positionIsLoading && positionId) {
            startFetchingData(LOADING_AREAS.positionContext);
        }
    }, [positionIsLoading, positionId, startFetchingData]);

    useEffect(() => {
        if (!positionIsLoading && positionId) {
            stopFetchingData(LOADING_AREAS.positionContext);
        }
    }, [positionIsLoading, positionId, stopFetchingData]);

    // Error handling
    useEffect(() => {
        if (positionError) {
            Logger.error(positionError);
            showErrorNotification('GET_POSITION_ERROR_NOTIFICATION');
        }
    }, [positionError, showErrorNotification]);

    // Uncheck candidates when position changes
    useEffect(() => {
        if (positionId) {
            uncheckAllCandidates();
        }
    }, [positionId, employer, uncheckAllCandidates]);

    const isPositionClosed = useMemo(() => [PositionStatuses.CLOSED_BY_ATS, PositionStatuses.CANCELLED].includes(positionStatus), [positionStatus]);

    const positionLastRatedSampleFromLastCalibration = useMemo(
        () => position?.lastRatedSampleFromLastCalibration || 0,
        [position?.lastRatedSampleFromLastCalibration],
    );

    const fastRatingSamples = useMemo(() => position?.fastRatingSamples || [], [position?.fastRatingSamples]);

    const fastRatingSamplesPersonalization = useMemo(
        () => (position?.fastRatingSamples || []).filter((s) => !!s.clonedFrom),
        [position?.fastRatingSamples],
    );

    // Modified by AI on 06/12/2026. Edit #8.
    const refreshProgressAIScore = useCallback(debounce(async (fetchedPosition) => {
        if (isEmpty(position) && !fetchedPosition) {
            return;
        }

        const currentPositionAIProgressScore = fetchedPosition
            ? get(fetchedPosition, 'settings.progressAIScore', 0)
            : positionAIProgressScore.current;

        const positionToSave = !isEmpty(fetchedPosition)
            ? fetchedPosition
            : position;

        const fetchedProgressAIScore = await PositionHttpService.getProgressAIScore(positionToSave._id);

        if (fetchedProgressAIScore < currentPositionAIProgressScore) {
            return;
        }

        positionAIProgressScore.current = fetchedProgressAIScore;

        if ((currentPositionAIProgressScore < fetchedProgressAIScore) && positionId) {
            await PositionHttpService.updateProgressAIScore(positionId, fetchedProgressAIScore);
            // Refetch position to get updated progress score
            await refetchPosition();
        }

        const isSufficientScoreReachedForTheFirstTime = (currentPositionAIProgressScore !== fetchedProgressAIScore)
            && (currentPositionAIProgressScore < progressAI.sufficientScoreInPercent)
            && (progressAI.sufficientScoreInPercent <= fetchedProgressAIScore);

        if (isSufficientScoreReachedForTheFirstTime) {
            trackEvent(TRACKING_EVENTS.POSITION_AI_SCORE_REACHED_SUFFICIENT_VALUE, {
                positionId: positionToSave._id,
                positionName: positionToSave.jobTitle,
                employerId: positionToSave.employerId,
            });
        }
    }, REFRESH_PROGRESS_AI_SCORE_DEBOUNCE_TIME_MS), [
        position,
        positionId,
        progressAI.sufficientScoreInPercent,
        trackEvent,
        refetchPosition,
    ]);

    // Modified by AI on 06/12/2026. Edit #6.
    const refreshPositionData = useCallback(async () => {
        if (positionId) {
            const { data } = await refetchPosition();
            return data;
        }
        return null;
    }, [positionId, refetchPosition]);

    // Modified by AI on 06/12/2026. Edit #9.
    const queryClient = useQueryClient();
    const setPosition = useCallback((newPositionData) => {
        if (positionId && newPositionData) {
            queryClient.setQueryData(QUERY_KEYS.position(positionId), newPositionData);
        }
    }, [positionId, queryClient]);

    const createPositionLocationsKey = (locations) => locations.map((loc) => loc.text).sort().join('-');

    const checkIfPositionIsGdprCompliant = useCallback(async ({ positionData: positionArg } = {}) => {
        const locations = ComplianceService.getPositionLocations(positionArg);
        const locationsKey = createPositionLocationsKey(locations);
        const isLocationsKeyUnchanged = positionGdprCheckRef.current.locationsKey === locationsKey;
        const isResultEmpty = positionGdprCheckRef.current.result === null;

        if (isLocationsKeyUnchanged && !isResultEmpty) {
            return positionGdprCheckRef.current.result;
        }

        const result = await ComplianceService.checkIfPositionIsGdprCompliant({ locations });
        positionGdprCheckRef.current = { locationsKey, result };

        return result;
    }, [position]);

    const getPositionGdprLocations = useCallback(async ({ positionData: positionArg } = {}) => {
        const locations = ComplianceService.getPositionLocations(positionArg);
        const locationsKey = createPositionLocationsKey(locations);
        const isLocationsKeyUnchanged = positionGdprLocationsRef.current.locationsKey === locationsKey;
        const isResultEmpty = positionGdprLocationsRef.current.result === null;

        if (isLocationsKeyUnchanged && !isResultEmpty) {
            return positionGdprLocationsRef.current.result;
        }

        const result = await ComplianceService.getPositionGdprLocations({ locations });
        positionGdprLocationsRef.current = { locationsKey, result };

        const gdprLocations = result.filter((l) => l.isGdpr);
        return gdprLocations;
    }, [position]);

    const getSuccessAccountManagerEmail = useCallback(async (_positionId) => {
        try {
            const accountManagerRecruiterEmail = await PositionHttpService.getAccountManagerEmail(_positionId);

            if (!accountManagerRecruiterEmail) {
                return;
            }

            setSuccessAccountManagerEmail(accountManagerRecruiterEmail);
        } catch (error) {
            Logger(error);
        }
    }, []);

    const atsJobUrl = useMemo(() => {
        const positionAtsJobUrl = get(position, 'atsSettings.atsJobUrl');
        const atsJobInternalId = get(position, 'atsSettings.atsJobInternalId');
        const atsJobId = get(position, 'atsSettings.atsJobId');

        if (!isEmpty(positionAtsJobUrl)) {
            return positionAtsJobUrl;
        }

        const atsJobUrlTemplate = chain(employer)
            .get('settings.atsJobUrlTemplate')
            .trim()
            .value();

        if (!isEmpty(atsJobUrlTemplate) && (atsJobInternalId || atsJobId)) {
            return chain(atsJobUrlTemplate)
                .replace('{jobId}', atsJobInternalId)
                .replace('{jobExternalId}', atsJobId)
                .value();
        }

        return null;
    }, [
        employer?.settings?.atsJobUrlTemplate,
        position?.atsSettings?.atsJobUrl,
        position?.atsSettings?.atsJobInternalId,
        position?.atsSettings?.atsJobId,
    ]);

    // Modified by AI on 06/12/2026. Edit #7.
    // Fetch account manager email (side effect, not core position data)
    useEffect(() => {
        if (positionId) {
            getSuccessAccountManagerEmail(positionId);
        }
    }, [positionId, getSuccessAccountManagerEmail]);

    // Modified by AI on 06/30/2026. Edit #2.
    const emitPositionEvent = useCallback((event) => {
        setPositionEventData(event);
        setPositionEventTrigger((prev) => prev + 1);
    }, []);

    const contextValue = useMemo(() => ({
        positionId,
        position,
        setPosition,
        refreshPositionData,
        positionEventTrigger,
        positionEventData,
        emitPositionEvent,
        refreshProgressAIScore,
        checkedCandidates,
        setCheckedCandidates,
        uncheckAllCandidates,
        removeCheckedCandidate,
        checkIfPositionIsGdprCompliant,
        getPositionGdprLocations,
        isPositionGdprCompliant,
        positionHasRemoteLocation,
        positionStatus,
        isPositionClosed,
        fastRatingSamples,
        fastRatingSamplesPersonalization,
        successAccountManagerEmail,
        getSuccessAccountManagerEmail,
        atsJobUrl,
        isAtsPosition,
        isPcPosition,
        isPositionCalibrated,
        isPositionInRecalibrationEditFlow,
        positionLastRatedSampleFromLastCalibration,
        isPositionInRecalibrationNewFlow,
        isPositionInManualSelectionStep,
        isPositionInManualMessagingStep,
        positionRecalibrationStatus,
        positionIsNotInRecalibrationFlow,
    }), [
        positionId,
        position,
        refreshPositionData,
        positionEventTrigger,
        positionEventData,
        emitPositionEvent,
        refreshProgressAIScore,
        checkedCandidates,
        checkIfPositionIsGdprCompliant,
        getPositionGdprLocations,
        isPositionGdprCompliant,
        positionHasRemoteLocation,
        positionStatus,
        isPositionClosed,
        fastRatingSamples,
        fastRatingSamplesPersonalization,
        successAccountManagerEmail,
        getSuccessAccountManagerEmail,
        uncheckAllCandidates,
        removeCheckedCandidate,
        atsJobUrl,
        isPositionCalibrated,
        isPositionInRecalibrationEditFlow,
        positionLastRatedSampleFromLastCalibration,
        isPositionInRecalibrationNewFlow,
        positionRecalibrationStatus,
        positionIsNotInRecalibrationFlow,
    ]);

    return (
        <PositionContextProvider value={contextValue}>
            {children}
        </PositionContextProvider>
    );
});

export default PositionContext;
